import React, { useEffect, useState } from "react";
import { Flag, Trash2, X } from "lucide-react";

const statusOptions = [
  { value: "not_started", label: "À lancer" },
  { value: "in_progress", label: "En cours" },
  { value: "blocked", label: "Bloqué" },
  { value: "done", label: "Terminé" },
];

const MilestoneObjectiveModal = ({
  open,
  objective,
  projectName,
  employees = [],
  defaultDate,
  saving = false,
  onClose,
  onSave,
  onDelete,
}) => {
  const [draft, setDraft] = useState({
    name: "",
    description: "",
    milestone_date: defaultDate || "",
    owner_id: "",
    status: "not_started",
  });
  const [formError, setFormError] = useState("");
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft({
      name: objective?.name || "",
      description: objective?.description || "",
      milestone_date: objective?.milestone_date || defaultDate || "",
      owner_id: objective?.owner_id ?? "",
      status: objective?.status || "not_started",
    });
    setFormError("");
    setConfirmingDelete(false);
  }, [defaultDate, objective, open]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose?.();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open, saving]);

  if (!open) return null;

  const updateField = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setFormError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!draft.name.trim()) {
      setFormError("Le nom du jalon est requis.");
      return;
    }
    if (!draft.milestone_date) {
      setFormError("La date du jalon est requise.");
      return;
    }
    try {
      await onSave?.({
        ...draft,
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        owner_id: draft.owner_id === "" ? null : Number(draft.owner_id),
      });
    } catch (error) {
      setFormError(error?.message || "Impossible d'enregistrer le jalon.");
    }
  };

  const handleDelete = async () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    try {
      await onDelete?.(objective);
    } catch (error) {
      setFormError(error?.message || "Impossible de supprimer le jalon.");
    }
  };

  const inputClass =
    "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Fermer"
        onClick={() => !saving && onClose?.()}
      />
      <form
        className="relative w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-2xl"
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="milestone-objective-title"
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rose-50 text-rose-600">
              <Flag className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2 id="milestone-objective-title" className="text-base font-semibold text-slate-900">
                {objective ? "Modifier le jalon" : "Nouvel objectif jalon"}
              </h2>
              {projectName && <p className="truncate text-sm text-slate-500">{projectName}</p>}
            </div>
          </div>
          <button
            type="button"
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            onClick={onClose}
            disabled={saving}
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_180px]">
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Nom de l'objectif
              <input
                type="text"
                value={draft.name}
                onChange={(event) => updateField("name", event.target.value)}
                className={inputClass}
                autoFocus
                disabled={saving}
              />
            </label>
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Date du jalon
              <input
                type="date"
                value={draft.milestone_date}
                onChange={(event) => updateField("milestone_date", event.target.value)}
                className={inputClass}
                disabled={saving}
              />
            </label>
          </div>

          <label className="block space-y-1.5 text-sm font-medium text-slate-700">
            Description
            <textarea
              value={draft.description}
              onChange={(event) => updateField("description", event.target.value)}
              className={`${inputClass} min-h-[96px] resize-y`}
              disabled={saving}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Owner
              <select
                value={draft.owner_id}
                onChange={(event) => updateField("owner_id", event.target.value)}
                className={inputClass}
                disabled={saving}
              >
                <option value="">Aucun owner</option>
                {employees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Statut
              <select
                value={draft.status}
                onChange={(event) => updateField("status", event.target.value)}
                className={inputClass}
                disabled={saving}
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {formError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {formError}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <div>
            {objective && (
              <button
                type="button"
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
                  confirmingDelete
                    ? "bg-rose-600 text-white hover:bg-rose-700"
                    : "text-rose-600 hover:bg-rose-100"
                }`}
                onClick={handleDelete}
                disabled={saving}
              >
                <Trash2 className="h-4 w-4" />
                {confirmingDelete ? "Confirmer la suppression" : "Supprimer"}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
              onClick={onClose}
              disabled={saving}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-wait disabled:opacity-60"
              disabled={saving}
            >
              <Flag className="h-4 w-4" />
              {saving ? "Enregistrement..." : objective ? "Enregistrer" : "Créer le jalon"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MilestoneObjectiveModal;
